import { useCallback, useEffect, useRef, useState } from 'react';
import { isPlainDate } from '@complan/domain';
import {
  salesCycleDerivationRequestSchema,
  type SalesCycle,
} from '@complan/contracts';
import { apiFetch } from '@/shared/api/client';

/**
 * Derives the Sales_Cycle set for an Initiative's timing, debounced against
 * `POST /api/sales-cycle-derivation` (R7.1-R7.3).
 *
 * ## Why this exists
 *
 * `deriveSalesCyclesForRange` (the server's pure intersection rule) and its
 * endpoint have existed since task 16.2, and the Intake Wizard's Timing step has
 * always carried a note promising the set "is derived from the dates" (task
 * 23.3's UI-spec mapping names this exact wiring as the wizard's "Sales_Cycle
 * proposal alert"). Nothing before this hook actually called the endpoint from
 * the wizard: `TimingStep` registered only `startDate`/`endDate`, so
 * `salesCycleAssignment` never left its form default of
 * `{ source: 'derived', salesCycleIds: [] }` and every save persisted zero
 * Sales_Cycle links regardless of the entered dates.
 *
 * ## Debounce, not on every keystroke
 *
 * A date field commits on blur/change like every other RHF-registered field
 * here, but re-deriving on every keystroke of a date `<input>` would fire a
 * request per digit typed. This hook takes the same 300 ms debounce
 * `useValidateInitiative` already uses for the server re-validation (R5.7), so
 * the derivation settles once the user pauses rather than mid-edit.
 *
 * ## Malformed or incomplete ranges
 *
 * A blank or invalid date (the form's `''` placeholder, or an end date not yet
 * later than the start) never reaches the network: {@link isPlainDate} gates the
 * call, matching `deriveSalesCyclesForRange`'s own stated behaviour that a
 * malformed range yields an empty set rather than a plausible-looking wrong one
 * - the difference here is simply not asking the server about a range that
 * cannot be resolved yet, so a half-typed date does not flash a stale proposal.
 */

/** The debounce window this hook waits behind (matches `VALIDATE_DEBOUNCE_MS`). */
export const DERIVE_DEBOUNCE_MS = 300;

export interface SalesCycleDerivationState {
  /** The derived Sales_Cycle set for the current dates, in the server's canonical order. */
  readonly derivedSalesCycles: readonly SalesCycle[];
  /** True once at least one derivation has resolved for the current dates. */
  readonly hasResolved: boolean;
  /** True while a debounced derivation is scheduled or in flight. */
  readonly isDeriving: boolean;
}

const INITIAL_STATE: SalesCycleDerivationState = {
  derivedSalesCycles: [],
  hasResolved: false,
  isDeriving: false,
};

/**
 * Schedules a debounced `POST /api/sales-cycle-derivation` call whenever
 * `startDate`/`endDate` change to a well-formed range, and reports the result.
 *
 * Takes the raw form field values (which are `''` before either date is
 * entered, matching {@link emptyIntakeValues}) rather than assuming they already
 * validate — the gate below is what makes an incomplete form safe to pass
 * through on every render.
 */
export function useSalesCycleDerivation(
  startDate: string | undefined,
  endDate: string | undefined,
): SalesCycleDerivationState {
  const [state, setState] = useState<SalesCycleDerivationState>(INITIAL_STATE);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic sequence so a slow earlier response cannot overwrite a later one,
  // the same guard `useValidateInitiative` uses.
  const sequenceRef = useRef(0);

  const runDerivation = useCallback(
    async (range: { readonly startDate: string; readonly endDate: string }, sequence: number): Promise<void> => {
      try {
        const body = salesCycleDerivationRequestSchema.parse(range);
        const raw = await apiFetch<{ derivedSalesCycles: readonly SalesCycle[] }>(
          '/api/sales-cycle-derivation',
          { method: 'POST', body: JSON.stringify(body) },
        );
        if (sequence !== sequenceRef.current) return; // superseded
        setState({
          derivedSalesCycles: raw.derivedSalesCycles,
          hasResolved: true,
          isDeriving: false,
        });
      } catch {
        if (sequence !== sequenceRef.current) return;
        // A transient failure leaves the last known derivation in place rather
        // than clearing a proposal the user was already shown; only the
        // in-flight indicator drops, matching `useValidateInitiative`'s
        // degradation.
        setState((current) => ({ ...current, isDeriving: false }));
      }
    },
    [],
  );

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    // Gate on well-formed dates before scheduling anything: a blank or
    // half-typed date must not reach the network, and must not silently keep a
    // stale proposal from a previous, now-abandoned range either.
    if (
      startDate === undefined ||
      endDate === undefined ||
      !isPlainDate(startDate) ||
      !isPlainDate(endDate) ||
      endDate < startDate
    ) {
      sequenceRef.current += 1; // invalidate any in-flight call for the old range
      setState(INITIAL_STATE);
      return;
    }

    setState((current) => ({ ...current, isDeriving: true }));
    const sequence = ++sequenceRef.current;
    timerRef.current = setTimeout(() => {
      void runDerivation({ startDate, endDate }, sequence);
    }, DERIVE_DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [startDate, endDate, runDerivation]);

  return state;
}
